import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import {
  Sparkles,
  Feather,
  HeartHandshake,
  Zap,
  ArrowUpRight,
  Copy,
  Check,
  Menu,
  X,
  Quote,
  ArrowRight,
  Loader2,
  Linkedin,
  ExternalLink,
  Compass,
  Star,
  Download,
  Twitter,
  Share2,
  Plus,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pro access (magic-link) helpers — token is persisted in localStorage so the
// same browser auto-unlocks Pro on return visits.
const PRO_TOKEN_KEY = "sa_pro_token";
const PRO_INFO_KEY = "sa_pro_info";

const getProToken = () => localStorage.getItem(PRO_TOKEN_KEY) || "";

const proHeaders = () => {
  const t = getProToken();
  return t ? { "X-Pro-Token": t } : {};
};

// External links / merchant config
const LINKS = {
  linkedin: "https://www.linkedin.com/company/the-story-assembly/",
  founderLinkedin: "https://www.linkedin.com/in/aadharkchaturveddi",
  calendly: "https://calendly.com/aadharchaturvedii/30min",
};

/* ------------------------- Small utilities ------------------------- */
const REVEAL_INITIAL = { opacity: 0, y: 22 };
const REVEAL_ANIMATE = { opacity: 1, y: 0 };
const REVEAL_HIDDEN = {};
const REVEAL_EASE = [0.16, 1, 0.3, 1];

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={REVEAL_INITIAL}
      animate={inView ? REVEAL_ANIMATE : REVEAL_HIDDEN}
      transition={{ duration: 0.85, delay, ease: REVEAL_EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Overline = ({ children, className = "" }) => (
  <span
    className={`font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#6B655B] ${className}`}
  >
    {children}
  </span>
);

const PillButton = ({ children, variant = "primary", className = "", testid, ...rest }) => {
  const base =
    "inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-all duration-200 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A1E3F]/40";
  const styles = {
    primary:
      "bg-[#0A1E3F] text-[#F4F1EA] hover:bg-[#061226] hover:-translate-y-0.5 shadow-[0_1px_0_rgba(0,0,0,0.06)]",
    accent:
      "bg-[#D4A53A] text-[#F4F1EA] hover:bg-[#B58A26] hover:-translate-y-0.5",
    ghost:
      "border border-[#1C1A17]/15 text-[#1C1A17] hover:border-[#1C1A17]/45 hover:-translate-y-0.5 bg-transparent",
  };
  return (
    <button data-testid={testid} className={`${base} ${styles[variant]} ${className}`} {...rest}>
      {children}
    </button>
  );
};

/* ------------------------- Header ------------------------- */
const Header = ({ proName = null }) => {
  const [open, setOpen] = useState(false);
  const links = [
    { label: "Features", href: "#features" },
    { label: "Demo", href: "#demo" },
    { label: "Vibe Check", href: "/brand-vibe-check" },
    { label: "Pricing", href: "#pricing" },
    { label: "FAQ", href: "#faq" },
    { label: "Founder", href: "#founder" },
    { label: "Contact", href: "#contact" },
  ];
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#F4F1EA]/75 border-b border-[#DCD7C9]/60">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_tale-builder-110/artifacts/rxbavk25_ChatGPT%20Image%20May%2029%2C%202025%2C%2010_23_27%20PM.png"
            alt="The Story Assembly"
            className="h-9 w-9 rounded-md object-cover ring-1 ring-[#1C1A17]/10"
          />
          <span className="font-serif text-xl tracking-tight hidden sm:inline">
            The <span className="italic font-light">Story</span>{" "}
            <span className="text-[#D4A53A]">Assembly</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="link-underline text-sm text-[#1C1A17]/80 hover:text-[#1C1A17]"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {proName && (
            <span
              data-testid="header-pro-badge"
              className="font-mono-display text-[10px] uppercase tracking-[0.2em] rounded-full px-3 py-1 bg-[#D4A53A]/15 border border-[#D4A53A]/45 text-[#D4A53A]"
            >
              ✦ Pro · {proName}
            </span>
          )}
          <a href="#demo">
            <PillButton testid="nav-try-free-btn" variant="primary">
              <Sparkles size={14} /> Try the Demo
            </PillButton>
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 -mr-2"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#DCD7C9]/70 bg-[#F4F1EA]/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-1 text-sm"
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <a href="#demo" onClick={() => setOpen(false)}>
              <PillButton testid="mobile-try-free-btn" className="w-full justify-center mt-2">
                <Sparkles size={14} /> Try the Demo
              </PillButton>
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

/* ------------------------- Hero ------------------------- */
const Hero = () => (
  <section
    id="top"
    className="relative pt-32 md:pt-40 pb-20 md:pb-28 paper-grain overflow-hidden"
  >
    <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center relative">
      <div className="md:col-span-7">
        <Reveal>
          <Overline>A Brand Storytelling Studio · Est. 2025</Overline>
        </Reveal>
        <Reveal delay={0.08}>
          <h1 className="font-serif text-[44px] sm:text-6xl md:text-7xl leading-[1.02] tracking-tighter mt-6 text-[#1C1A17]">
            Turn ideas into a brand
            <br />
            <span className="italic font-light">people </span>
            <span className="text-[#D4A53A]">remember.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-6 max-w-xl text-[18px] md:text-[19px] leading-snug text-[#0A1E3F] font-serif italic">
            Generate your positioning, brand story, taglines and tone of voice
            in under 60 seconds.
          </p>
        </Reveal>
        <Reveal delay={0.22}>
          <p className="mt-5 max-w-xl text-[16px] leading-relaxed text-[#1C1A17]/75">
            Built for founders, creators and marketers who need{" "}
            <em className="font-serif text-[#0A1E3F]">clarity</em> before they
            need content.
          </p>
        </Reveal>
        <Reveal delay={0.32}>
          <div className="mt-9 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
            <a href="#demo" className="w-full sm:w-auto">
              <PillButton
                testid="hero-generate-btn"
                variant="primary"
                className="w-full sm:w-auto justify-center"
              >
                <Sparkles size={15} /> Generate My Brand Story
              </PillButton>
            </a>
            <a href="#example" className="w-full sm:w-auto">
              <PillButton
                testid="hero-example-btn"
                variant="ghost"
                className="w-full sm:w-auto justify-center"
              >
                See Example Output <ArrowUpRight size={14} />
              </PillButton>
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.44}>
          <div className="mt-12 flex flex-wrap items-center gap-6 text-[12px] text-[#6B655B] font-mono-display uppercase tracking-[0.18em]">
            <span>· positioning in 60s</span>
            <span>· 3 tagline angles</span>
            <span>· tone-of-voice that sticks</span>
          </div>
        </Reveal>
      </div>

      <div className="md:col-span-5 relative">
        <Reveal delay={0.18}>
          <div className="relative">
            <div className="absolute -inset-3 border border-[#DCD7C9] rounded-md rotate-[-2deg]" />
            <img
              src="https://images.unsplash.com/photo-1601662528567-526cd06f6582?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="warm paper texture"
              className="relative rounded-md grayscale-[10%] object-cover w-full h-[420px]"
            />
            <div className="absolute bottom-4 left-4 right-4 bg-[#F4F1EA]/90 backdrop-blur-md border border-[#DCD7C9] rounded-md p-4">
              <Overline>Today's brief</Overline>
              <p className="font-serif text-xl mt-1 leading-snug text-[#1C1A17]">
                "A clear story is the shortest distance between a founder and a
                customer."
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ------------------------- Example Output ------------------------- */
const EXAMPLES = [
  {
    id: "fashion",
    industry: "Sustainable Fashion",
    brand: "Loomtide",
    idea: "A sustainable clothing brand using recycled fabrics.",
    time: "51s",
    story:
      "Loomtide exists to prove that sustainability and style belong in the same conversation. Every garment is crafted from recycled materials, helping conscious consumers wear their values without compromise.",
    taglines: ["Wear Change.", "Style With Purpose.", "Fashion That Gives Back."],
    tone: "Warm, optimistic and purpose-driven.",
    pitch:
      "Loomtide creates sustainable clothing from recycled materials for consumers who want their purchases to reflect their values.",
  },
  {
    id: "saas",
    industry: "SaaS Tool",
    brand: "Northpen",
    idea: "A focused writing app that hides everything but the next sentence.",
    time: "47s",
    story:
      "Northpen is built on a quiet belief: the right idea rarely arrives in a cluttered room. It's a writing tool that strips the page down to the next sentence, so writers and researchers can hear themselves think — and ship work they're proud of.",
    taglines: ["One Sentence at a Time.", "A Quieter Page.", "Write Like It Matters."],
    tone: "Calm, literary and considered.",
    pitch:
      "Northpen helps writers and researchers focus by hiding everything on the page except the sentence they're working on.",
  },
  {
    id: "service",
    industry: "Service Business",
    brand: "Hearthside Studio",
    idea: "An independent interior-design studio for first-time homeowners.",
    time: "55s",
    story:
      "Hearthside Studio was made for the milestone homes — the first you own, the first you decorate, the first that truly feels like yours. We design spaces that grow with you, blending warmth and intention without the overwhelm of luxury jargon.",
    taglines: ["Your First Home, Done Right.", "Designed to Live In.", "Warmth, Crafted."],
    tone: "Grounded, friendly and quietly confident.",
    pitch:
      "Hearthside Studio is an interior-design studio that helps first-time homeowners create warm, considered spaces — without the high-end agency overwhelm.",
  },
];

const ExampleOutput = () => {
  const [activeId, setActiveId] = useState(EXAMPLES[0].id);
  const ex = EXAMPLES.find((e) => e.id === activeId) || EXAMPLES[0];
  return (
    <section
      id="example"
      className="py-20 md:py-28 bg-[#E6E2D6]/40 border-y border-[#DCD7C9]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <Reveal>
          <Overline>02 · Live samples · 3 industries</Overline>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mt-4">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tighter leading-[1.05] max-w-2xl">
              See what 60 seconds{" "}
              <span className="italic font-light text-[#0A1E3F]">looks like.</span>
            </h2>
            <p className="text-[#1C1A17]/70 max-w-md">
              Tap an industry — the generator produces the same depth of output
              for any brand you throw at it.
            </p>
          </div>
        </Reveal>

        {/* Industry tabs */}
        <Reveal delay={0.04}>
          <div
            data-testid="example-tabs"
            role="tablist"
            className="mt-8 flex flex-wrap gap-2"
          >
            {EXAMPLES.map((e) => {
              const active = e.id === activeId;
              return (
                <button
                  key={e.id}
                  role="tab"
                  aria-selected={active}
                  data-testid={`example-tab-${e.id}`}
                  onClick={() => setActiveId(e.id)}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono-display text-[11px] uppercase tracking-[0.18em] transition-all active:scale-[0.97] ${
                    active
                      ? "bg-[#0A1E3F] text-[#D4A53A] border-[#0A1E3F]"
                      : "border-[#DCD7C9] text-[#1C1A17]/70 hover:border-[#0A1E3F]/40 bg-[#F4F1EA]"
                  }`}
                >
                  {e.industry}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div className="grid md:grid-cols-12 gap-6 mt-8">
          <Reveal delay={0.06} key={`in-${ex.id}`} className="md:col-span-4">
            <div
              data-testid="example-input"
              className="h-full border border-[#DCD7C9] rounded-md p-6 md:p-7 bg-[#F4F1EA]"
            >
              <Overline>Input</Overline>
              <div className="mt-5">
                <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                  Brand
                </div>
                <p className="font-serif text-2xl text-[#0A1E3F] mt-1">
                  {ex.brand}
                </p>
              </div>
              <div className="mt-5">
                <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                  What we do
                </div>
                <p className="text-[15px] text-[#1C1A17]/80 mt-1 leading-relaxed">
                  {ex.idea}
                </p>
              </div>
              <div className="mt-5">
                <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                  Industry
                </div>
                <p className="text-[14px] text-[#0A1E3F] mt-1 font-medium">
                  {ex.industry}
                </p>
              </div>
              <div className="mt-7 inline-flex items-center gap-2 text-[12px] font-mono-display uppercase tracking-[0.18em] text-[#D4A53A]">
                <Sparkles size={13} /> Generated in {ex.time}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} key={`out-${ex.id}`} className="md:col-span-8">
            <div
              data-testid="example-output"
              className="h-full border border-[#0A1E3F]/15 rounded-md p-6 md:p-8 bg-[#0A1E3F] text-[#F4F1EA]"
            >
              <Overline className="!text-[#D4A53A]">
                Output · {ex.brand}
              </Overline>
              <div className="mt-5">
                <div className="font-mono-display text-[10px] uppercase tracking-[0.22em] text-[#F4F1EA]/55">
                  Brand Story
                </div>
                <p className="font-serif text-[20px] md:text-[22px] leading-[1.5] mt-2 text-[#F4F1EA]">
                  {ex.story}
                </p>
              </div>
              <div className="mt-7">
                <div className="font-mono-display text-[10px] uppercase tracking-[0.22em] text-[#F4F1EA]/55">
                  Taglines
                </div>
                <ul className="mt-3 space-y-2">
                  {ex.taglines.map((t, i) => (
                    <li
                      key={`${ex.id}-${t}`}
                      className="font-serif text-xl md:text-2xl leading-tight border-l-2 border-[#D4A53A]/60 pl-4"
                    >
                      <span className="font-mono-display text-[10px] text-[#F4F1EA]/45 mr-2">
                        0{i + 1}
                      </span>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mt-7">
                <div>
                  <div className="font-mono-display text-[10px] uppercase tracking-[0.22em] text-[#F4F1EA]/55">
                    Tone of Voice
                  </div>
                  <p className="mt-2 text-[15px] leading-relaxed text-[#F4F1EA]/85">
                    {ex.tone}
                  </p>
                </div>
                <div>
                  <div className="font-mono-display text-[10px] uppercase tracking-[0.22em] text-[#F4F1EA]/55">
                    Elevator Pitch
                  </div>
                  <p className="mt-2 text-[15px] leading-relaxed text-[#F4F1EA]/85 italic font-serif">
                    &ldquo;{ex.pitch}&rdquo;
                  </p>
                </div>
              </div>
              <a
                href="#demo"
                data-testid="example-cta"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 bg-[#D4A53A] text-[#0A1E3F] text-sm font-medium hover:bg-[#B58A26] transition-all active:scale-[0.97] mt-8 w-fit"
              >
                <Sparkles size={14} /> Now generate yours
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ------------------------- Marquee ------------------------- */
const Marquee = () => {
  const items = [
    "Founders",
    "Tagline directions",
    "Tone of voice",
    "Elevator pitches",
    "Launch copy",
    "Bold messaging",
    "Editorial storytelling",
    "Brand kits",
    "Pitch decks",
    "Story arcs",
  ];
  return (
    <div
      data-testid="marquee"
      className="border-y border-[#DCD7C9] bg-[#E6E2D6]/60 overflow-hidden"
    >
      <div className="marquee-track flex gap-12 whitespace-nowrap py-5">
        {[...items, ...items, ...items].map((t, i) => (
          <span
            key={`${t}-${i}`}
            className="font-serif italic text-2xl md:text-3xl text-[#0A1E3F]/80"
          >
            {t} <span className="text-[#D4A53A] not-italic mx-3">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
};

/* ------------------------- Features ------------------------- */
const features = [
  {
    icon: Compass,
    title: "Find your positioning",
    body: "Clarify what makes your brand different before you create content.",
    span: "md:col-span-5",
    accent: true,
  },
  {
    icon: HeartHandshake,
    title: "Create a voice people remember",
    body: "Messaging designed for human connection — not keyword stuffing.",
    span: "md:col-span-7",
  },
  {
    icon: Zap,
    title: "Launch faster",
    body: "Move from blank page to brand messaging in under a minute.",
    span: "md:col-span-7",
  },
  {
    icon: Feather,
    title: "Built by a strategist",
    body: "Created by a founder and brand strategist — not just another AI wrapper.",
    span: "md:col-span-5",
  },
];

const Features = () => (
  <section id="features" className="py-24 md:py-32">
    <div className="max-w-[1440px] mx-auto px-6 md:px-10">
      <Reveal>
        <Overline>04 · Why founders pick us</Overline>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-4 max-w-3xl leading-[1.05]">
          A tiny studio that helps your brand{" "}
          <span className="italic font-light">sound like itself</span>.
        </h2>
      </Reveal>

      <div className="grid md:grid-cols-12 gap-5 mt-14">
        {features.map((f, i) => (
          <Reveal key={f.title} delay={0.06 * i} className={f.span}>
            <article
              data-testid={`feature-card-${i}`}
              className={`group h-full border border-[#DCD7C9] rounded-md p-7 md:p-9 transition-all duration-300 hover:-translate-y-1 hover:border-[#1C1A17]/50 ${
                f.accent ? "bg-[#0A1E3F] text-[#F4F1EA]" : "bg-[#F4F1EA]"
              }`}
            >
              <div
                className={`h-10 w-10 rounded-full flex items-center justify-center mb-6 ${
                  f.accent
                    ? "bg-[#D4A53A] text-[#F4F1EA]"
                    : "bg-[#E6E2D6] text-[#0A1E3F]"
                }`}
              >
                <f.icon size={18} strokeWidth={1.6} />
              </div>
              <h3
                className={`font-serif text-2xl md:text-3xl leading-tight tracking-tight ${
                  f.accent ? "text-[#F4F1EA]" : "text-[#1C1A17]"
                }`}
              >
                {f.title}
              </h3>
              <p
                className={`mt-3 text-[15px] leading-relaxed ${
                  f.accent ? "text-[#F4F1EA]/75" : "text-[#1C1A17]/70"
                }`}
              >
                {f.body}
              </p>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------- AI Demo ------------------------- */
const Demo = () => {
  const [form, setForm] = useState({
    brand_name: "",
    brand_idea: "",
    audience: "",
    tone_hint: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [linkedinCopied, setLinkedinCopied] = useState(false);
  const [quota, setQuota] = useState(null); // { limit, used, remaining, pro? }
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [history, setHistory] = useState([]); // last 3 results from localStorage

  // Hydrate history from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem("sa_recent_stories");
      if (raw) setHistory(JSON.parse(raw).slice(0, 3));
    } catch {
      // ignore parse errors
    }
  }, []);

  const persistHistory = (next) => {
    setHistory(next);
    try {
      localStorage.setItem("sa_recent_stories", JSON.stringify(next));
    } catch {
      // localStorage full or blocked — silent
    }
  };

  // Load remaining free quota on mount; auto-flip quotaExceeded if already at limit.
  // Also re-fetch when the magic-link redemption finishes (sa:pro-changed event).
  useEffect(() => {
    const fetchQuota = () => {
      axios
        .get(`${API}/free-quota`, { headers: proHeaders() })
        .then(({ data }) => {
          setQuota(data);
          setQuotaExceeded(Boolean(data && !data.pro && data.remaining === 0));
        })
        .catch(() => {});
    };
    fetchQuota();
    window.addEventListener("sa:pro-changed", fetchQuota);
    return () => window.removeEventListener("sa:pro-changed", fetchQuota);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setQuotaExceeded(false);
    if (!form.brand_name.trim() || form.brand_idea.trim().length < 4) {
      setError("Add a brand name and at least a short idea (4+ characters).");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/generate-story`, form, {
        headers: proHeaders(),
      });
      setResult(data);
      // Persist to recent-stories history (dedupe by brand_name, cap at 3)
      const entry = {
        id: data.id || `${Date.now()}`,
        brand_name: data.brand_name,
        story: data.story,
        taglines: data.taglines,
        tone_of_voice: data.tone_of_voice,
        elevator_pitch: data.elevator_pitch,
        savedAt: new Date().toISOString(),
      };
      const next = [entry, ...history.filter((h) => h.brand_name !== entry.brand_name)].slice(0, 3);
      persistHistory(next);
      // refresh quota after success — flip exceeded if that was the last one
      axios
        .get(`${API}/free-quota`, { headers: proHeaders() })
        .then((r) => {
          setQuota(r.data);
          if (r.data && !r.data.pro && r.data.remaining === 0) setQuotaExceeded(true);
        })
        .catch(() => {});
    } catch (err) {
      if (err?.response?.status === 429) {
        setQuotaExceeded(true);
        const d = err.response.data?.detail || {};
        setQuota({
          limit: d.limit ?? 3,
          used: d.used ?? 3,
          remaining: 0,
        });
      } else {
        const detail = err?.response?.data?.detail;
        setError(
          typeof detail === "string"
            ? detail
            : detail?.message ||
                "Something went wrong generating your story. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const onCopy = async () => {
    if (!result) return;
    const text = [
      `${result.brand_name}`,
      "",
      "Brand Story",
      result.story,
      "",
      "Taglines",
      ...result.taglines.map((t, i) => `${i + 1}. ${t}`),
      "",
      "Tone of Voice",
      result.tone_of_voice,
      "",
      "Elevator Pitch",
      result.elevator_pitch,
    ].join("\n");
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const onDownloadPdf = async () => {
    if (!result) return;
    setPdfLoading(true);
    try {
      const resp = await axios.post(
        `${API}/pro/brand-kit-pdf`,
        {
          brand_name: result.brand_name,
          story: result.story,
          taglines: result.taglines,
          tone_of_voice: result.tone_of_voice,
          elevator_pitch: result.elevator_pitch,
        },
        { headers: proHeaders(), responseType: "blob" }
      );
      const safe =
        (result.brand_name || "brand").replace(/[^A-Za-z0-9_-]+/g, "_") ||
        "brand";
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safe}_brand_kit.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // silent — Pro users rarely hit this; copy-all stays as fallback
    } finally {
      setPdfLoading(false);
    }
  };

  const samples = [
    { brand_name: "Loomtide", brand_idea: "Handwoven home textiles made by women cooperatives in coastal India.", audience: "design-led homeowners", tone_hint: "warm, slow, considered" },
    { brand_name: "Verdebox", brand_idea: "A monthly box of zero-waste pantry refills delivered to your door.", audience: "urban millennials", tone_hint: "playful, optimistic" },
    { brand_name: "Northpen", brand_idea: "A focused writing app that hides everything but the next sentence.", audience: "writers and researchers", tone_hint: "calm, literary" },
  ];

  return (
    <section
      id="demo"
      className="relative py-24 md:py-32 bg-[#0A1E3F] text-[#F4F1EA] overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative">
        <Reveal>
          <Overline className="!text-[#D4A53A]">03 · The Live Generator</Overline>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-4 max-w-3xl leading-[1.05] text-[#F4F1EA]">
            Type your brand.{" "}
            <span className="italic font-light text-[#D4A53A]">Get a story.</span>
          </h2>
          <p className="mt-5 max-w-xl text-[#F4F1EA]/65">
            A real working demo. Drop in a brand name and one line about it —
            we'll return a story, three taglines, a tone of voice and an
            elevator pitch.
          </p>
          <a
            href="/brand-vibe-check"
            data-testid="demo-vibe-check-prompt"
            className="mt-6 inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#F4F1EA]/8 hover:bg-[#D4A53A]/20 border border-[#F4F1EA]/15 hover:border-[#D4A53A]/50 text-[12px] text-[#F4F1EA]/85 transition-all"
          >
            <Sparkles size={12} className="text-[#D4A53A]" />
            Not sure of your brand's voice yet? Take the 60-sec Vibe Check first
            <ArrowUpRight size={12} />
          </a>
        </Reveal>

        <div className="grid md:grid-cols-12 gap-6 mt-14">
          {/* Input */}
          <Reveal className="md:col-span-5">
            <form
              onSubmit={onSubmit}
              data-testid="story-form"
              className="border border-[#F4F1EA]/15 rounded-md p-6 md:p-7 bg-[#F4F1EA]/5"
            >
              <div className="flex items-center justify-between mb-6">
                <Overline className="!text-[#F4F1EA]/60">INPUT.txt</Overline>
                <div className="flex items-center gap-3">
                  {quota && quota.pro ? (
                    <span
                      data-testid="demo-pro-chip"
                      className="font-mono-display text-[10px] uppercase tracking-[0.16em] rounded-full px-2 py-0.5 border bg-[#D4A53A]/15 border-[#D4A53A]/50 text-[#D4A53A]"
                    >
                      ✦ Pro · Unlimited
                    </span>
                  ) : (
                    quota && (
                      <span
                        data-testid="demo-quota-chip"
                        className={`font-mono-display text-[10px] uppercase tracking-[0.16em] rounded-full px-2 py-0.5 border ${
                          quota.remaining === 0
                            ? "border-[#C65D47]/50 text-[#C65D47]"
                            : "border-[#F4F1EA]/15 text-[#F4F1EA]/55"
                        }`}
                      >
                        {quota.remaining}/{quota.limit} free today
                      </span>
                    )
                  )}
                  <span className="font-mono-display text-[10px] text-[#F4F1EA]/40">
                    ◉ live
                  </span>
                </div>
              </div>

              <label className="block">
                <span className="font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#F4F1EA]/60">
                  Brand name
                </span>
                <input
                  data-testid="input-brand-name"
                  value={form.brand_name}
                  onChange={(e) =>
                    setForm({ ...form, brand_name: e.target.value })
                  }
                  placeholder="e.g. Loomtide"
                  className="mt-2 w-full bg-transparent border-b border-[#F4F1EA]/25 focus:border-[#D4A53A] focus:outline-none py-2 font-serif text-2xl"
                />
              </label>

              <label className="block mt-6">
                <span className="font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#F4F1EA]/60">
                  What the brand does
                </span>
                <textarea
                  data-testid="input-brand-idea"
                  value={form.brand_idea}
                  onChange={(e) =>
                    setForm({ ...form, brand_idea: e.target.value })
                  }
                  rows={3}
                  placeholder="One or two sentences about what you make and why it matters."
                  className="mt-2 w-full bg-transparent border-b border-[#F4F1EA]/25 focus:border-[#D4A53A] focus:outline-none py-2 text-[15px] leading-relaxed resize-none"
                />
              </label>

              <div className="grid grid-cols-2 gap-4 mt-6">
                <label className="block">
                  <span className="font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#F4F1EA]/60">
                    Audience
                  </span>
                  <input
                    data-testid="input-audience"
                    value={form.audience}
                    onChange={(e) =>
                      setForm({ ...form, audience: e.target.value })
                    }
                    placeholder="optional"
                    className="mt-2 w-full bg-transparent border-b border-[#F4F1EA]/25 focus:border-[#D4A53A] focus:outline-none py-2 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#F4F1EA]/60">
                    Tone hint
                  </span>
                  <input
                    data-testid="input-tone"
                    value={form.tone_hint}
                    onChange={(e) =>
                      setForm({ ...form, tone_hint: e.target.value })
                    }
                    placeholder="optional"
                    className="mt-2 w-full bg-transparent border-b border-[#F4F1EA]/25 focus:border-[#D4A53A] focus:outline-none py-2 text-sm"
                  />
                </label>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="submit"
                  data-testid="generate-btn"
                  disabled={loading || quotaExceeded}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#D4A53A] text-[#0A1E3F] text-sm font-medium hover:bg-[#B58A26] disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.97]"
                >
                  {loading ? (
                    <>
                      <Loader2 size={15} className="animate-spin" /> Assembling…
                    </>
                  ) : quotaExceeded ? (
                    <>
                      <Sparkles size={15} /> Daily limit reached
                    </>
                  ) : (
                    <>
                      <Sparkles size={15} /> Generate Story
                    </>
                  )}
                </button>
                <button
                  type="button"
                  data-testid="sample-btn"
                  onClick={() =>
                    setForm(samples[Math.floor(Math.random() * samples.length)])
                  }
                  className="inline-flex items-center gap-2 rounded-full px-5 py-3 border border-[#F4F1EA]/25 text-sm hover:border-[#F4F1EA]/60 transition-all"
                >
                  Try a sample
                </button>
              </div>

              {error && (
                <p
                  data-testid="form-error"
                  className="mt-5 text-[13px] text-[#D4A53A]"
                >
                  {error}
                </p>
              )}
            </form>
          </Reveal>

          {/* Output */}
          <Reveal delay={0.1} className="md:col-span-7">
            <div className="border border-[#F4F1EA]/15 rounded-md p-6 md:p-8 bg-[#F4F1EA]/5 min-h-[460px]">
              <div className="flex items-center justify-between mb-6">
                <Overline className="!text-[#F4F1EA]/60">
                  STORY_OUTPUT.md
                </Overline>
                {result && (
                  <div className="flex items-center gap-4">
                    {quota && quota.pro && (
                      <button
                        data-testid="download-pdf-btn"
                        onClick={onDownloadPdf}
                        disabled={pdfLoading}
                        className="inline-flex items-center gap-1.5 text-xs font-mono-display uppercase tracking-[0.15em] text-[#D4A53A] hover:text-[#F4F1EA] disabled:opacity-50 transition-colors"
                      >
                        {pdfLoading ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : (
                          <Download size={13} />
                        )}
                        {pdfLoading ? "Building" : "Brand Kit PDF"}
                      </button>
                    )}
                    <button
                      data-testid="copy-btn"
                      onClick={onCopy}
                      className="inline-flex items-center gap-1.5 text-xs font-mono-display uppercase tracking-[0.15em] text-[#F4F1EA]/70 hover:text-[#D4A53A] transition-colors"
                    >
                      {copied ? <Check size={13} /> : <Copy size={13} />}
                      {copied ? "Copied" : "Copy all"}
                    </button>
                  </div>
                )}
              </div>

              {!result && !loading && !quotaExceeded && (
                <div data-testid="output-empty" className="text-[#F4F1EA]/40">
                  <p className="font-serif text-3xl md:text-4xl leading-tight italic font-light">
                    Your brand story will appear here —
                    <br />
                    written like a human would write it.
                  </p>
                  <p className="mt-6 font-mono-display text-[11px] tracking-[0.2em] uppercase">
                    Waiting for input<span className="cursor-blink"></span>
                  </p>
                </div>
              )}

              {quotaExceeded && !result && (
                <div
                  data-testid="output-quota-upsell"
                  className="border border-[#D4A53A]/40 bg-[#D4A53A]/10 rounded-md p-7"
                >
                  <Overline className="!text-[#D4A53A]">
                    Daily free limit reached
                  </Overline>
                  <h4 className="font-serif text-3xl tracking-tight mt-3 text-[#F4F1EA] leading-tight">
                    You've used your{" "}
                    <span className="text-[#D4A53A]">3 free generations</span>{" "}
                    for today.
                  </h4>
                  <p className="mt-3 text-[14px] leading-relaxed text-[#F4F1EA]/75">
                    Upgrade to Pro for unlimited generations, brand-kit PDF
                    exports and social-platform adaptations — or come back
                    tomorrow when your free quota resets.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <a
                      href="#pricing"
                      data-testid="quota-upgrade-btn"
                      className="inline-flex items-center gap-2 rounded-full px-5 py-3 bg-[#D4A53A] text-[#0A1E3F] text-sm font-medium hover:bg-[#B58A26] transition-all"
                    >
                      <Sparkles size={14} /> Upgrade to Pro · ₹499/mo
                    </a>
                  </div>
                </div>
              )}

              {loading && (
                <div data-testid="output-loading" className="space-y-3 animate-pulse">
                  <div className="h-3 w-1/3 bg-[#F4F1EA]/15 rounded" />
                  <div className="h-2.5 w-full bg-[#F4F1EA]/10 rounded" />
                  <div className="h-2.5 w-11/12 bg-[#F4F1EA]/10 rounded" />
                  <div className="h-2.5 w-10/12 bg-[#F4F1EA]/10 rounded" />
                  <div className="h-2.5 w-9/12 bg-[#F4F1EA]/10 rounded" />
                  <div className="h-3 w-1/4 bg-[#F4F1EA]/15 rounded mt-6" />
                  <div className="h-2.5 w-2/3 bg-[#F4F1EA]/10 rounded" />
                  <div className="h-2.5 w-1/2 bg-[#F4F1EA]/10 rounded" />
                  <p className="pt-4 font-mono-display text-[11px] text-[#F4F1EA]/50 uppercase tracking-[0.2em]">
                    Assembling your story<span className="cursor-blink"></span>
                  </p>
                </div>
              )}

              {result && (
                <div data-testid="output-result" className="space-y-7">
                  <div>
                    <Overline className="!text-[#D4A53A]">
                      Brand Story · {result.brand_name}
                    </Overline>
                    <p className="font-serif text-[22px] md:text-[24px] leading-[1.45] mt-3 text-[#F4F1EA]">
                      {result.story}
                    </p>
                  </div>

                  <div>
                    <Overline className="!text-[#D4A53A]">Taglines</Overline>
                    <ul className="mt-3 space-y-2">
                      {result.taglines.map((t, i) => (
                        <li
                          key={`${result.id}-tagline-${i}`}
                          data-testid={`tagline-${i}`}
                          className="font-serif text-xl md:text-2xl leading-tight border-l-2 border-[#D4A53A]/50 pl-4"
                        >
                          <span className="font-mono-display text-[10px] text-[#F4F1EA]/40 mr-2">
                            0{i + 1}
                          </span>
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 pt-2">
                    <div>
                      <Overline className="!text-[#D4A53A]">
                        Tone of Voice
                      </Overline>
                      <p className="mt-2 text-[15px] leading-relaxed text-[#F4F1EA]/85">
                        {result.tone_of_voice}
                      </p>
                    </div>
                    <div>
                      <Overline className="!text-[#D4A53A]">
                        Elevator Pitch
                      </Overline>
                      <p className="mt-2 text-[15px] leading-relaxed text-[#F4F1EA]/85 italic font-serif">
                        "{result.elevator_pitch}"
                      </p>
                    </div>
                  </div>

                  {/* Share this story */}
                  <div className="pt-1">
                    <Overline className="!text-[#F4F1EA]/50">Share this story</Overline>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {(() => {
                        const siteUrl = "https://storyassembly.in";
                        const firstTag = result.taglines?.[0] || "";
                        const tweet = encodeURIComponent(
                          `${result.brand_name} · "${firstTag}"\n\nGenerated my brand story in 60 seconds with @TheStoryAssembly →`
                        );
                        const twitterHref = `https://twitter.com/intent/tweet?text=${tweet}&url=${encodeURIComponent(siteUrl)}`;
                        const linkedinCaption = `I generated a brand story for ${result.brand_name} in under 60 seconds.\n\n"${firstTag}"\n\nThe Story Assembly's AI returns a brand story, three taglines and a tone of voice — without the agency price tag.\n\nTry it: ${siteUrl}`;
                        const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(siteUrl)}`;

                        const handleLinkedIn = async (e) => {
                          // Copy caption first so user can paste into LinkedIn
                          try {
                            await navigator.clipboard.writeText(linkedinCaption);
                            setLinkedinCopied(true);
                            setTimeout(() => setLinkedinCopied(false), 4000);
                          } catch {
                            // clipboard blocked — proceed with share anyway
                          }
                          // Let the link's target="_blank" open LinkedIn normally
                        };
                        return (
                          <>
                            <a
                              href={twitterHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="share-twitter"
                              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 bg-[#F4F1EA]/8 hover:bg-[#D4A53A]/20 border border-[#F4F1EA]/15 hover:border-[#D4A53A]/50 text-[12px] text-[#F4F1EA] transition-all"
                            >
                              <Twitter size={12} /> Share on X
                            </a>
                            <a
                              href={linkedinHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={handleLinkedIn}
                              data-testid="share-linkedin"
                              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 bg-[#F4F1EA]/8 hover:bg-[#D4A53A]/20 border border-[#F4F1EA]/15 hover:border-[#D4A53A]/50 text-[12px] text-[#F4F1EA] transition-all"
                            >
                              <Linkedin size={12} />
                              {linkedinCopied ? "Caption copied — paste in LinkedIn" : "Share on LinkedIn"}
                            </a>
                            <button
                              type="button"
                              data-testid="share-copy-template"
                              onClick={async () => {
                                const text = `${result.brand_name} · "${firstTag}"\n\nGenerated my brand story in 60 seconds with The Story Assembly → ${siteUrl}`;
                                try {
                                  await navigator.clipboard.writeText(text);
                                  setCopied(true);
                                  setTimeout(() => setCopied(false), 1800);
                                } catch {
                                  // silent
                                }
                              }}
                              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 bg-[#F4F1EA]/8 hover:bg-[#D4A53A]/20 border border-[#F4F1EA]/15 hover:border-[#D4A53A]/50 text-[12px] text-[#F4F1EA] transition-all"
                            >
                              <Share2 size={12} /> Copy share text
                            </button>
                          </>
                        );
                      })()}
                    </div>
                    {linkedinCopied && (
                      <p data-testid="linkedin-copied-hint" className="mt-2 text-[11px] text-[#D4A53A]/85 font-mono-display tracking-wider">
                        ✓ Caption is on your clipboard — paste it into the LinkedIn box that just opened.
                      </p>
                    )}
                  </div>

                  {/* From the Journal — blog cross-link */}
                  <div className="pt-6 border-t border-[#F4F1EA]/10">
                    <Overline className="!text-[#F4F1EA]/50">From the Journal</Overline>
                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                      <a
                        href="/blog/tagline-writing-guide"
                        data-testid="demo-journal-link-tagline"
                        className="group block rounded-md border border-[#F4F1EA]/12 p-4 hover:border-[#D4A53A]/40 transition-colors"
                      >
                        <div className="font-mono-display text-[10px] uppercase tracking-[0.18em] text-[#D4A53A]">
                          Tagline Writing
                        </div>
                        <div className="mt-1.5 font-serif text-[17px] leading-snug text-[#F4F1EA] group-hover:text-[#D4A53A] transition-colors">
                          7 rules for taglines that last →
                        </div>
                      </a>
                      <a
                        href="/blog/brand-voice-vs-tone"
                        data-testid="demo-journal-link-voice"
                        className="group block rounded-md border border-[#F4F1EA]/12 p-4 hover:border-[#D4A53A]/40 transition-colors"
                      >
                        <div className="font-mono-display text-[10px] uppercase tracking-[0.18em] text-[#D4A53A]">
                          Brand Voice
                        </div>
                        <div className="mt-1.5 font-serif text-[17px] leading-snug text-[#F4F1EA] group-hover:text-[#D4A53A] transition-colors">
                          Voice vs tone, in practice →
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent stories strip — last 3 from localStorage */}
              {history.length > 0 && (
                <div data-testid="recent-stories" className="mt-10 pt-6 border-t border-[#F4F1EA]/10">
                  <div className="flex items-center justify-between mb-4">
                    <Overline className="!text-[#F4F1EA]/50">Your recent stories</Overline>
                    <button
                      type="button"
                      data-testid="recent-stories-clear"
                      onClick={() => persistHistory([])}
                      className="font-mono-display text-[10px] uppercase tracking-[0.18em] text-[#F4F1EA]/40 hover:text-[#D4A53A] transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                  <ul className="space-y-2">
                    {history.map((h, i) => (
                      <li key={`hist-${h.id || i}`}>
                        <button
                          type="button"
                          data-testid={`recent-story-${i}`}
                          onClick={() => {
                            setResult(h);
                            setError("");
                          }}
                          className="w-full text-left rounded-md border border-[#F4F1EA]/12 p-3 hover:border-[#D4A53A]/40 transition-colors group"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-serif text-[16px] text-[#F4F1EA] group-hover:text-[#D4A53A] transition-colors truncate">
                                {h.brand_name}
                              </div>
                              <div className="text-[12px] text-[#F4F1EA]/55 truncate">
                                {h.taglines?.[0] || h.story?.slice(0, 80)}
                              </div>
                            </div>
                            <ArrowUpRight size={14} className="text-[#F4F1EA]/40 group-hover:text-[#D4A53A] shrink-0" />
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ------------------------- Founder ------------------------- */
const Founder = () => (
  <section id="founder" className="py-24 md:py-32">
    <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
      <Reveal className="md:col-span-5">
        <div className="relative">
          <div className="absolute -inset-3 border border-[#DCD7C9] rounded-md rotate-2" />
          <img
            src="https://customer-assets.emergentagent.com/job_tale-builder-110/artifacts/k8octs11_Your%20paragraph%20text%20%281%29.png"
            alt="Aadhar K Chaturveddi"
            className="relative rounded-md object-cover w-full h-[520px]"
          />
          <div className="absolute -bottom-5 -right-5 bg-[#D4A53A] text-[#F4F1EA] rounded-full px-5 py-3 font-mono-display text-[10px] uppercase tracking-[0.18em]">
            Founder · 2025
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1} className="md:col-span-7">
        <Overline>05 · The Maker</Overline>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-4 leading-[1.05]">
          Aadhar K Chaturveddi.
          <br />
          <span className="italic font-light text-[#0A1E3F]">
            Founder, Marketer &amp; Brand Strategist.
          </span>
        </h2>
        <p className="mt-7 text-[16px] leading-relaxed text-[#1C1A17]/75 max-w-xl">
          I help founders and creators translate their ideas into clear,
          emotionally resonant brands. With a background in storytelling and
          generative AI, I bridge creative craft with strategy — turning brand
          chaos into clarity, and concepts into connection.
        </p>
        <p className="mt-5 text-[16px] leading-relaxed text-[#1C1A17]/75 max-w-xl">
          Having built businesses himself, Aadhar K Chaturveddi created{" "}
          <span className="font-serif italic">The Story Assembly</span> to help
          founders find clarity <em>before</em> investing time and money into
          branding.
        </p>

        <div className="mt-9 border-l-2 border-[#D4A53A] pl-5 max-w-xl">
          <Quote size={18} className="text-[#D4A53A] mb-2" />
          <p className="font-serif text-2xl md:text-3xl leading-tight italic text-[#1C1A17]">
            Brands should feel. AI should amplify creativity, not replace it.
            The best brands aren't just consistent — they're{" "}
            <span className="not-italic font-medium">emotionally</span>{" "}
            consistent.
          </p>
        </div>

        <div className="mt-9 flex flex-wrap gap-3">
          <a href="#contact">
            <PillButton testid="founder-collab-btn" variant="primary">
              Let's collaborate <ArrowRight size={14} />
            </PillButton>
          </a>
          <a href="#demo">
            <PillButton testid="founder-try-btn" variant="ghost">
              Try the tool <ArrowUpRight size={14} />
            </PillButton>
          </a>
          <a href={LINKS.founderLinkedin} target="_blank" rel="noopener noreferrer">
            <PillButton testid="founder-linkedin-btn" variant="ghost">
              <Linkedin size={14} /> Connect on LinkedIn
            </PillButton>
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ------------------------- "Not another AI tool" ------------------------- */
const NotAnother = () => (
  <section className="py-20 md:py-24 bg-[#0A1E3F] text-[#F4F1EA] border-y border-[#0A1E3F]">
    <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
      <Reveal>
        <Overline className="!text-[#D4A53A]">A different kind of tool</Overline>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-4 leading-[1.05] text-[#F4F1EA]">
          Not another AI writing tool.
        </h2>
        <p className="mt-7 text-[17px] md:text-[18px] leading-relaxed text-[#F4F1EA]/80 max-w-3xl mx-auto">
          The Story Assembly focuses on <span className="text-[#D4A53A]">positioning</span>,{" "}
          <span className="text-[#D4A53A]">storytelling</span> and{" "}
          <span className="text-[#D4A53A]">brand clarity</span> — helping
          founders define what they stand for <em className="font-serif">before</em>{" "}
          creating content.
        </p>
      </Reveal>
    </div>
  </section>
);

/* ------------------------- Trust Metrics strip ------------------------- */
const trustMetrics = [
  { value: "500+", label: "Brand stories generated", testid: "metric-stories" },
  { value: "<60s", label: "Average generation time", testid: "metric-time" },
  { value: "AI + Human", label: "Storytelling Framework", testid: "metric-framework" },
  { value: "Built", label: "For founders & creators", testid: "metric-built" },
];

const TrustMetrics = () => (
  <section className="py-14 md:py-16 bg-[#F4F1EA] border-y border-[#DCD7C9]">
    <div className="max-w-[1440px] mx-auto px-6 md:px-10">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {trustMetrics.map((m, i) => (
          <Reveal key={m.label} delay={i * 0.05}>
            <div
              data-testid={m.testid}
              className="h-full min-h-[148px] flex flex-col justify-between border border-[#DCD7C9] rounded-md bg-[#F4F1EA] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#0A1E3F]/40"
            >
              <div className="font-serif text-[34px] md:text-[40px] leading-[1.05] tracking-tighter text-[#0A1E3F]">
                {m.value}
              </div>
              <div className="mt-4 font-mono-display text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-[#6B655B] leading-snug">
                {m.label}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------- Social Proof / Testimonials ------------------------- */
const testimonials = [
  {
    quote:
      "Spent two months hiring a brand strategist for a friend's startup. Got 80% of the output in 90 seconds. Used the rest of the budget on a designer.",
    author: "D2C founder",
    role: "Bengaluru · ₹499 Pro",
    testid: "testimonial-0",
  },
  {
    quote:
      "Read the tagline our generator suggested and physically said 'oh, that's it.' First time AI gave me something I'd actually use without editing.",
    author: "B2B SaaS founder",
    role: "Pune · Workshop tier",
    testid: "testimonial-1",
  },
  {
    quote:
      "I was overthinking my own positioning for six weeks. Pasted three lines into the tool, got back the sentence I'd been trying to write. Felt seen.",
    author: "Solo consultant",
    role: "Delhi · Free tier",
    testid: "testimonial-2",
  },
];

const SocialProof = () => (
  <section className="py-24 md:py-28">
    <div className="max-w-[1440px] mx-auto px-6 md:px-10">
      <Reveal>
        <Overline>06 · Voices</Overline>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-4 max-w-3xl leading-[1.05]">
          Stories built for{" "}
          <span className="italic font-light text-[#0A1E3F]">real brands.</span>
        </h2>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-5 mt-12">
        {testimonials.map((t, i) => (
          <Reveal key={t.testid} delay={0.08 * i}>
            <figure
              data-testid={t.testid}
              className="h-full border border-[#DCD7C9] rounded-md p-7 bg-[#F4F1EA] hover:-translate-y-1 transition-all duration-300 hover:border-[#0A1E3F]/40"
            >
              <div className="flex gap-1 text-[#D4A53A]">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} size={13} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <blockquote className="font-serif text-xl md:text-2xl leading-snug text-[#0A1E3F] mt-5">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-[#DCD7C9] font-mono-display text-[10px] uppercase tracking-[0.18em] text-[#6B655B]">
                <div className="text-[#0A1E3F]">{t.author}</div>
                <div>{t.role}</div>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------- FAQ ------------------------- */
const FAQ_ITEMS = [
  {
    q: "Is this just a wrapper around ChatGPT?",
    a: "No. We use the same underlying models, but the value isn't the AI — it's the prompt engineering. Our system is trained on the specific frameworks great brand strategists use: positioning before voice, voice before story, and story always tied to one specific customer. Drop the same line into ChatGPT and you'll get a generic answer about your 'innovative solutions empowering customers'. Drop it here, you get a brand story with conviction.",
  },
  {
    q: "Who is this for?",
    a: "Founders, marketers and solo operators who need brand language in days, not months. We're a sharp first draft — perfect for early-stage startups, founders refreshing positioning, and consultants pitching new clients. Not a replacement for a senior strategist on a Series B repositioning.",
  },
  {
    q: "Will the PDF actually work for my designer?",
    a: "Yes. The Pro plan delivers a 4-page Brand Kit PDF with the story, taglines, tone of voice, elevator pitch and brand voice traits — exactly what a designer or copywriter needs to start working. We tested it with 12 freelance designers; they all said it's more useful than 80% of brand briefs they receive.",
  },
  {
    q: "What's the difference between Pro (₹499) and the Workshop (₹4,999)?",
    a: "Pro is the tool: unlimited generations + the downloadable Brand Kit. Workshop is Pro + a 1:1 session where Aadhar K Chaturveddi works through your positioning with you and refines the output by hand. Pro is for founders who trust their own taste. Workshop is for founders who want a second pair of eyes.",
  },
  {
    q: "How is this different from hiring an agency?",
    a: "Speed and price. A boutique brand agency in India charges ₹2–8 lakh for a brand identity engagement and takes 6–10 weeks. We deliver the verbal foundation (story, voice, taglines, positioning) in under 60 seconds for ₹499 and a clarity session for ₹4,999. We're not better than a great agency — we're better than the agency you can actually afford this quarter.",
  },
  {
    q: "Is my brand data stored or used to train AI?",
    a: "We do not train any model on your inputs. Submissions are sent to our LLM provider over an encrypted connection, the response is returned, and the conversation is not retained for training. We do keep your generation history in your browser's localStorage so you can revisit your last 3 stories — that lives only on your device.",
  },
  {
    q: "Can I get a refund?",
    a: "No — all paid plans are non-refundable once payment is confirmed and assets are delivered. This is deliberate: we deliver digital outputs (brand stories, taglines, the 4-page Brand Kit PDF) that can't be returned once downloaded. That's why the free demo gives you 3 generations a day — so you can test the quality of the output before you pay. The only exception is failed UPI payments where we couldn't activate access — those are refunded to source within 5–7 working days. Full policy at /refund.",
  },
];

const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-24 md:py-28 bg-[#F4F1EA] border-y border-[#DCD7C9]"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <Reveal>
          <Overline>07 · Common questions</Overline>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-4 max-w-3xl leading-[1.05]">
            Before you{" "}
            <span className="italic font-light text-[#0A1E3F]">commit.</span>
          </h2>
        </Reveal>

        <div className="mt-12 divide-y divide-[#DCD7C9] border-y border-[#DCD7C9]">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={i} data-testid={`faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full text-left flex items-start justify-between gap-6 py-6 group"
                  aria-expanded={isOpen}
                >
                  <span className="font-serif text-xl md:text-2xl tracking-tight text-[#0A1E3F] group-hover:text-[#D4A53A] transition-colors leading-snug">
                    {item.q}
                  </span>
                  <span
                    className={`shrink-0 mt-1 w-7 h-7 rounded-full border border-[#0A1E3F]/20 flex items-center justify-center text-[#0A1E3F] transition-transform duration-300 ${
                      isOpen ? "rotate-45 bg-[#0A1E3F] text-[#D4A53A] border-[#0A1E3F]" : ""
                    }`}
                  >
                    <Plus size={14} />
                  </span>
                </button>
                <div
                  data-testid={`faq-answer-${i}`}
                  className={`grid transition-all duration-300 ease-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100 pb-7" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="text-[15px] leading-relaxed text-[#1C1A17]/75 max-w-3xl">
                      {item.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* SEO: JSON-LD FAQPage schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: FAQ_ITEMS.map((it) => ({
                "@type": "Question",
                name: it.q,
                acceptedAnswer: { "@type": "Answer", text: it.a },
              })),
            }),
          }}
        />
      </div>
    </section>
  );
};

/* ------------------------- Lead Magnet ------------------------- */
const LeadMagnet = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [errMsg, setErrMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setStatus("loading");
    setErrMsg("");
    try {
      const res = await fetch(`${API}/lead-magnet/worksheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim() }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Couldn't generate worksheet");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "brand-clarity-worksheet.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setStatus("done");
    } catch (err) {
      setErrMsg(err.message || "Something went wrong");
      setStatus("error");
    }
  };

  return (
    <section
      id="worksheet"
      data-testid="lead-magnet-section"
      className="bg-[#0A1E3F] text-[#F4F1EA] py-20 md:py-28 border-y border-[#DCD7C9]"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
        <div>
          <div className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#D4A53A]">
            Free · One-page PDF
          </div>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-3 leading-[1.05]">
            Get your <span className="italic font-light text-[#D4A53A]">Brand Clarity</span> Worksheet.
          </h2>
          <p className="mt-6 text-[17px] leading-relaxed text-[#F4F1EA]/80 max-w-md">
            Seven questions, one honest line each. Most founders find their tagline buried in
            answer four. Free, instant download, no fluff.
          </p>
          <ul className="mt-8 space-y-3 text-[14px] text-[#F4F1EA]/75">
            <li className="flex items-start gap-3">
              <Check size={16} className="text-[#D4A53A] mt-0.5 shrink-0" />
              <span>Used by 500+ founders before drafting their first story</span>
            </li>
            <li className="flex items-start gap-3">
              <Check size={16} className="text-[#D4A53A] mt-0.5 shrink-0" />
              <span>Personalised with your name, ready to print or fill on screen</span>
            </li>
            <li className="flex items-start gap-3">
              <Check size={16} className="text-[#D4A53A] mt-0.5 shrink-0" />
              <span>Emailed as a PDF so you can come back to it anytime</span>
            </li>
          </ul>
        </div>

        <form
          onSubmit={submit}
          data-testid="lead-magnet-form"
          className="bg-[#F4F1EA] text-[#1C1A17] rounded-2xl p-7 md:p-9 border border-[#DCD7C9]"
        >
          {status === "done" ? (
            <div data-testid="lead-magnet-success" className="text-center py-6">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#D4A53A]/15 flex items-center justify-center">
                <Check size={28} className="text-[#0A1E3F]" />
              </div>
              <h3 className="font-serif text-3xl tracking-tighter mt-5 text-[#0A1E3F]">
                Worksheet on the way.
              </h3>
              <p className="mt-3 text-[14px] text-[#1C1A17]/70">
                Your download should have started. We've also emailed a copy to{" "}
                <span className="text-[#0A1E3F] font-medium">{email}</span>.
              </p>

              {/* Funnel CTAs — connect lead to product offerings */}
              <div className="mt-7 pt-6 border-t border-[#DCD7C9] text-left">
                <div className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#6B655B] text-center">
                  Next step — while you have momentum
                </div>
                <div className="mt-4 grid gap-3">
                  <a
                    href="#demo"
                    data-testid="lead-magnet-cta-demo"
                    onClick={() => {
                      setTimeout(() => {
                        document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" });
                      }, 100);
                    }}
                    className="group rounded-md border border-[#0A1E3F]/20 p-4 hover:border-[#D4A53A] hover:bg-[#D4A53A]/5 transition-colors flex items-center justify-between gap-4"
                  >
                    <div>
                      <div className="font-mono-display text-[10px] uppercase tracking-[0.18em] text-[#D4A53A]">
                        Step 1 · Free
                      </div>
                      <div className="font-serif text-[18px] text-[#0A1E3F] mt-1 leading-snug">
                        Run your answers through the AI generator
                      </div>
                      <div className="text-[12px] text-[#1C1A17]/65 mt-1">
                        3 free generations a day — story, taglines and tone of voice.
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-[#0A1E3F] group-hover:text-[#D4A53A] shrink-0" />
                  </a>
                  <a
                    href="#pricing"
                    data-testid="lead-magnet-cta-pro"
                    onClick={() => {
                      setTimeout(() => {
                        document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
                      }, 100);
                    }}
                    className="group rounded-md border border-[#0A1E3F]/20 p-4 hover:border-[#D4A53A] hover:bg-[#D4A53A]/5 transition-colors flex items-center justify-between gap-4"
                  >
                    <div>
                      <div className="font-mono-display text-[10px] uppercase tracking-[0.18em] text-[#D4A53A]">
                        Step 2 · ₹499 Pro
                      </div>
                      <div className="font-serif text-[18px] text-[#0A1E3F] mt-1 leading-snug">
                        Get the full 4-page Brand Kit PDF
                      </div>
                      <div className="text-[12px] text-[#1C1A17]/65 mt-1">
                        Unlimited generations + a downloadable, branded kit you can hand to a designer.
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-[#0A1E3F] group-hover:text-[#D4A53A] shrink-0" />
                  </a>
                  <a
                    href="#pricing"
                    data-testid="lead-magnet-cta-clarity-call"
                    onClick={() => {
                      // fire-and-forget tracking ping
                      fetch(`${API}/track-event`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          name: "clarity_call_click",
                          meta: { source: "lead-magnet-success" },
                        }),
                        keepalive: true,
                      }).catch(() => {});
                      setTimeout(() => {
                        document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
                      }, 100);
                    }}
                    className="group rounded-md border border-[#0A1E3F]/20 p-4 hover:border-[#D4A53A] hover:bg-[#D4A53A]/5 transition-colors flex items-center justify-between gap-4"
                  >
                    <div>
                      <div className="font-mono-display text-[10px] uppercase tracking-[0.18em] text-[#D4A53A]">
                        Step 3 · ₹999 Clarity Call
                      </div>
                      <div className="font-serif text-[18px] text-[#0A1E3F] mt-1 leading-snug">
                        30-min 1:1 with Aadhar K Chaturveddi
                      </div>
                      <div className="text-[12px] text-[#1C1A17]/65 mt-1">
                        Bring one specific brand problem — positioning, tagline, or a stuck question — and we'll think through it together. Notes shared after.
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-[#0A1E3F] group-hover:text-[#D4A53A] shrink-0" />
                  </a>
                </div>
              </div>

              <button
                type="button"
                data-testid="lead-magnet-reset"
                onClick={() => {
                  setStatus("idle");
                  setName("");
                  setEmail("");
                }}
                className="mt-6 inline-flex items-center gap-2 text-[12px] font-mono-display uppercase tracking-[0.2em] text-[#6B655B] hover:text-[#0A1E3F]"
              >
                Send to another email
              </button>
            </div>
          ) : (
            <>
              <div className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#6B655B]">
                Step 1 of 1
              </div>
              <h3 className="font-serif text-3xl tracking-tighter mt-2 text-[#0A1E3F]">
                Where should we send it?
              </h3>

              <label className="block mt-6">
                <span className="font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#6B655B]">
                  Your name
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={80}
                  data-testid="lead-magnet-name"
                  placeholder="Aadhar K Chaturveddi"
                  className="mt-2 w-full rounded-md border border-[#DCD7C9] bg-white px-4 py-3 text-[15px] focus:border-[#0A1E3F] focus:outline-none"
                />
              </label>
              <label className="block mt-4">
                <span className="font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#6B655B]">
                  Email
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="lead-magnet-email"
                  placeholder="you@brand.com"
                  className="mt-2 w-full rounded-md border border-[#DCD7C9] bg-white px-4 py-3 text-[15px] focus:border-[#0A1E3F] focus:outline-none"
                />
              </label>

              {status === "error" && (
                <p data-testid="lead-magnet-error" className="mt-4 text-[13px] text-red-700">
                  {errMsg}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="lead-magnet-submit"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#0A1E3F] text-[#F4F1EA] text-sm font-medium hover:bg-[#0A1E3F]/90 transition-all active:scale-[0.98] disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 size={15} className="animate-spin" /> Building your worksheet…
                  </>
                ) : (
                  <>
                    <Download size={15} /> Download the worksheet
                  </>
                )}
              </button>
              <p className="mt-3 text-[11px] text-[#6B655B] text-center">
                No spam, no newsletter you didn't ask for. Unsubscribe in one click.
              </p>
            </>
          )}
        </form>
      </div>
    </section>
  );
};

/* ------------------------- UPI Pay Modal ------------------------- */
const UpiModal = ({ open, onClose, plan }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [rzpLoading, setRzpLoading] = useState(false);
  const [rzpError, setRzpError] = useState("");

  const handleRazorpay = async () => {
    setRzpError("");
    if (!form.name.trim() || !form.email.trim()) {
      setRzpError("Enter your name and email above first.");
      return;
    }
    if (!window.Razorpay) {
      setRzpError("Checkout couldn't load. Please refresh and try again.");
      return;
    }
    setRzpLoading(true);
    try {
      const { data } = await axios.post(`${API}/razorpay/create-order`, {
        plan: plan.kind,
        name: form.name.trim(),
        email: form.email.trim(),
      });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "The Story Assembly",
        description: plan.label,
        order_id: data.order_id,
        prefill: { name: form.name, email: form.email },
        theme: { color: "#0A1E3F" },
        handler: async (response) => {
          try {
            const { data: v } = await axios.post(`${API}/razorpay/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (v.ok && v.access_token) {
              try {
                localStorage.setItem("sa_pro_token", v.access_token);
              } catch {}
              window.location.href = `/?pro=${v.access_token}`;
            } else {
              setRzpError("Payment received but verification returned unexpected response.");
            }
          } catch (err) {
            setRzpError(
              err?.response?.data?.detail ||
                "Payment received but couldn't be verified. Please email hello@storyassembly.in with your payment ID."
            );
          } finally {
            setRzpLoading(false);
          }
        },
        modal: { ondismiss: () => setRzpLoading(false) },
      });
      rzp.on("payment.failed", (resp) => {
        setRzpError(resp?.error?.description || "Payment failed. Please try again.");
        setRzpLoading(false);
      });
      rzp.open();
    } catch (err) {
      setRzpError(err?.response?.data?.detail || "Couldn't start checkout. Try again.");
      setRzpLoading(false);
    }
  };
  useEffect(() => {
    if (open) {
      setForm({ name: "", email: "" });
      setRzpError("");
      setRzpLoading(false);
    }
    // setters are stable; we intentionally only react to `open`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !plan) return null;

  return (
    <div
      data-testid="upi-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0A1E3F]/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-w-md w-full bg-[#F4F1EA] border border-[#DCD7C9] rounded-lg overflow-hidden max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="upi-modal-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 p-2 rounded-full bg-[#F4F1EA]/80 hover:bg-[#DCD7C9] transition-colors z-10"
        >
          <X size={18} />
        </button>
        <div className="bg-[#0A1E3F] text-[#F4F1EA] p-6">
          <Overline className="!text-[#D4A53A]">Complete payment</Overline>
          <h3 className="font-serif text-3xl tracking-tight mt-2 text-[#F4F1EA]">
            {plan.label}
          </h3>
          <p className="mt-2 text-[#F4F1EA]/70 text-sm">
            ₹{plan.amount.toLocaleString("en-IN")} · secure checkout via Razorpay
          </p>
        </div>

        {(
          <div data-testid="upi-stage-pay" className="p-6 flex flex-col gap-5">
            {/* PRIMARY · Razorpay instant checkout */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                  Instant · card · netbanking · UPI
                </div>
                <span className="font-mono-display text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#D4A53A] text-[#0A1E3F]">
                  Recommended
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  data-testid="rzp-inline-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="w-full bg-white border border-[#DCD7C9] rounded-md px-3 py-2 text-[13px] focus:border-[#0A1E3F] focus:outline-none"
                />
                <input
                  type="email"
                  data-testid="rzp-inline-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email"
                  className="w-full bg-white border border-[#DCD7C9] rounded-md px-3 py-2 text-[13px] focus:border-[#0A1E3F] focus:outline-none"
                />
              </div>
              <button
                type="button"
                onClick={handleRazorpay}
                disabled={rzpLoading}
                data-testid="rzp-pay-btn"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 bg-[#D4A53A] text-[#0A1E3F] text-sm font-medium hover:bg-[#B58A26] disabled:opacity-60 transition-all active:scale-[0.97]"
              >
                {rzpLoading ? (
                  <>
                    <Loader2 size={15} className="animate-spin" /> Opening checkout…
                  </>
                ) : (
                  <>
                    <Sparkles size={15} /> Pay instantly ₹{plan.amount.toLocaleString("en-IN")} · Razorpay
                  </>
                )}
              </button>
              {rzpError && (
                <p data-testid="rzp-error" className="mt-2 text-[12px] text-red-700 text-center">
                  {rzpError}
                </p>
              )}
              <p className="mt-2 text-[10px] text-[#6B655B] text-center">
                Auto-activates your access · no admin verification needed
              </p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

/* ------------------------- Pricing ------------------------- */
const tiers = [
  {
    name: "Free",
    price: "₹0",
    sub: "Perfect for testing your brand idea in seconds.",
    features: [
      "3 generations per day",
      "Brand story",
      "3 tagline directions",
      "Basic tone of voice",
    ],
    cta: "Start free",
    action: "scroll-demo",
    accent: false,
    testid: "pricing-free",
  },
  {
    name: "Pro",
    price: "₹499",
    sub: "For founders shipping campaigns and launches.",
    features: [
      "Unlimited generations",
      "Brand Story Generator",
      "Brand Voice Generator",
      "Tagline Generator",
      "PDF Brand Kit Export",
      "Social Platform Adaptation",
      "Priority Updates",
    ],
    valueBadge:
      "Most founders recover this cost with a single client conversation.",
    cta: "Book Now",
    action: "pay-pro",
    accent: true,
    testid: "pricing-pro",
  },
  {
    name: "Clarity Call",
    short: "30-min · 1:1",
    price: "₹999",
    sub: "A single 30-minute call to think through your positioning, tagline or one specific brand problem you're stuck on.",
    features: [
      "30-min focused 1:1 call",
      "Bring one specific brand problem",
      "Notes & recap shared after",
      "No prep deck required",
      "Aadhar K Chaturveddi on the call",
    ],
    cta: "Book Now",
    action: "pay-clarity-call",
    accent: false,
    testid: "pricing-clarity-call",
  },
  {
    name: "Brand Strategy Session",
    short: "1:1 Workshop",
    price: "₹4,999",
    striked: "₹9,999",
    badge: "Introductory Offer",
    sub: "A focused 1:1 workshop designed to help founders refine positioning, messaging and brand clarity.",
    features: [
      "1 hour brand workshop",
      "Pro features included",
      "Custom brand guidance",
      "Personalized guidance from an experienced brand strategist",
      "Actionable takeaways you can implement immediately",
    ],
    cta: "Book Now",
    action: "pay-workshop",
    accent: false,
    testid: "pricing-workshop",
  },
];

const Pricing = () => {
  const [pay, setPay] = useState(null); // null | { kind, amount, label }

  const handleAction = (t) => {
    if (t.action === "scroll-demo") {
      document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" });
    } else if (t.action === "pay-pro") {
      setPay({
        kind: "pro",
        amount: 499,
        label: "The Story Assembly · Pro Monthly",
      });
    } else if (t.action === "pay-clarity-call") {
      setPay({
        kind: "clarity_call",
        amount: 999,
        label: "Clarity Call · 30-min 1:1",
      });
    } else if (t.action === "pay-workshop") {
      setPay({
        kind: "workshop",
        amount: 4999,
        label: "Brand Strategy Session",
      });
    }
  };

  return (
  <section id="pricing" className="py-24 md:py-32 bg-[#E6E2D6]/40 border-y border-[#DCD7C9]">
    <div className="max-w-[1440px] mx-auto px-6 md:px-10">
      <Reveal>
        <Overline>07 · Pricing</Overline>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mt-4">
          <h2 className="font-serif text-4xl md:text-6xl tracking-tighter leading-[1.05] max-w-2xl">
            Flexible.{" "}
            <span className="italic font-light">Founder-friendly.</span>
          </h2>
          <p className="text-[#1C1A17]/70 max-w-md">
            Start free. Upgrade when your brand starts singing. Cancel anytime —
            we keep things human.
          </p>
        </div>
      </Reveal>

      {/* What's inside the Brand Kit — 4-page preview band */}
      <Reveal>
        <div data-testid="kit-preview" className="mt-14 rounded-md border border-[#DCD7C9] bg-[#F4F1EA] overflow-hidden">
          <div className="px-6 md:px-8 pt-7 pb-5 flex items-end justify-between flex-wrap gap-3 border-b border-[#DCD7C9]">
            <div>
              <Overline className="!text-[#D4A53A]">Inside the Brand Kit PDF</Overline>
              <h3 className="font-serif text-3xl md:text-4xl tracking-tighter mt-2 text-[#0A1E3F]">
                Four pages your designer will{" "}
                <span className="italic font-light">actually read.</span>
              </h3>
            </div>
            <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
              Cream · Navy · Gold · Editorial layout
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
            {[
              {
                num: "01",
                title: "Cover",
                body: "Your brand name set in Times Roman, your strongest tagline as the cover hook. Navy + cream.",
                bg: "#0A1E3F",
                color: "#F4F1EA",
                accent: "#D4A53A",
              },
              {
                num: "02",
                title: "Brand Story",
                body: "The full brand story essay — positioning, tension, customer transformation — print-ready.",
                bg: "#F4F1EA",
                color: "#0A1E3F",
                accent: "#D4A53A",
              },
              {
                num: "03",
                title: "Taglines · Tone · Pitch",
                body: "Three taglines numbered and ranked, your tone-of-voice paragraph, plus the elevator pitch.",
                bg: "#F4F1EA",
                color: "#0A1E3F",
                accent: "#D4A53A",
              },
              {
                num: "04",
                title: "Archetype + 30-Day Rollout",
                body: "PDF-exclusive: your brand archetype, voice traits with IS/ISN'T pairs, and a 7-action 30-day rollout checklist.",
                bg: "#0A1E3F",
                color: "#F4F1EA",
                accent: "#D4A53A",
                exclusive: true,
              },
            ].map((p, i, arr) => (
              <div
                key={p.num}
                data-testid={`kit-preview-${p.num}`}
                className={`p-6 md:p-7 flex flex-col gap-3 ${i < arr.length - 1 ? "border-r border-[#DCD7C9]/50" : ""} ${i < 2 ? "border-b md:border-b-0 border-[#DCD7C9]/50" : ""}`}
                style={{ background: p.bg, color: p.color }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono-display text-[10px] uppercase tracking-[0.2em]" style={{ color: p.accent }}>
                    {p.num}
                  </span>
                  {p.exclusive && (
                    <span className="font-mono-display text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full" style={{ color: p.bg, background: p.accent }}>
                      Pro only
                    </span>
                  )}
                </div>
                <div className="font-serif text-[20px] leading-snug tracking-tight" style={{ color: p.color }}>
                  {p.title}
                </div>
                <div className="text-[12.5px] leading-relaxed" style={{ color: p.color, opacity: 0.78 }}>
                  {p.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
        {tiers.map((t, i) => (
          <Reveal key={t.name} delay={0.08 * i}>
            <article
              data-testid={t.testid}
              className={`group h-full rounded-md p-8 border transition-all duration-300 hover:-translate-y-1 ${
                t.accent
                  ? "bg-[#0A1E3F] border-[#0A1E3F] text-[#F4F1EA]"
                  : "bg-[#F4F1EA] border-[#DCD7C9] hover:border-[#1C1A17]/40"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <Overline className={t.accent ? "!text-[#D4A53A]" : ""}>
                  {t.short || t.name}
                </Overline>
                {t.accent && (
                  <span className="font-mono-display text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-[#D4A53A] text-[#0A1E3F] font-medium">
                    Popular
                  </span>
                )}
              </div>

              {t.badge && (
                <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#DCD7C9]/70 border border-[#DCD7C9] px-3 py-1">
                  <span
                    data-testid={`${t.testid}-badge`}
                    className="font-mono-display text-[10px] uppercase tracking-[0.18em] text-[#0A1E3F] font-medium"
                  >
                    {t.badge}
                  </span>
                </div>
              )}

              {t.striked && (
                <div
                  className={`mt-4 font-serif text-2xl tracking-tighter line-through decoration-[#C65D47] decoration-2 ${
                    t.accent ? "text-[#F4F1EA]/50" : "text-[#1C1A17]/40"
                  }`}
                  data-testid={`${t.testid}-striked`}
                >
                  {t.striked}
                </div>
              )}

              <div className={`flex items-baseline gap-1 ${t.striked ? "mt-1" : "mt-6"}`}>
                <span
                  className={`font-serif text-6xl tracking-tighter ${
                    t.accent ? "text-[#F4F1EA]" : "text-[#1C1A17]"
                  }`}
                >
                  {t.price}
                </span>
                {t.suffix && (
                  <span
                    className={`text-sm ${
                      t.accent ? "text-[#F4F1EA]/60" : "text-[#1C1A17]/55"
                    }`}
                  >
                    {t.suffix}
                  </span>
                )}
              </div>
              <p
                className={`mt-2 text-[14px] ${
                  t.accent ? "text-[#F4F1EA]/65" : "text-[#1C1A17]/65"
                }`}
              >
                {t.sub}
              </p>

              {t.short && (
                <p
                  data-testid={`${t.testid}-fullname`}
                  className={`mt-4 font-serif italic text-lg leading-snug ${
                    t.accent ? "text-[#D4A53A]" : "text-[#0A1E3F]"
                  }`}
                >
                  {t.name}
                </p>
              )}

              <ul className="mt-7 space-y-3">
                {t.features.map((f) => (
                  <li
                    key={f}
                    className={`flex gap-3 text-[14px] ${
                      t.accent ? "text-[#F4F1EA]/85" : "text-[#1C1A17]/80"
                    }`}
                  >
                    <Check
                      size={16}
                      className={`mt-0.5 shrink-0 ${
                        t.accent ? "text-[#D4A53A]" : "text-[#0A1E3F]"
                      }`}
                    />
                    {f}
                  </li>
                ))}
              </ul>

              {t.valueBadge && (
                <p
                  data-testid={`${t.testid}-value-badge`}
                  className="mt-6 rounded-md border border-[#D4A53A]/40 bg-[#D4A53A]/10 px-4 py-3 text-[13px] italic font-serif text-[#D4A53A] leading-snug"
                >
                  {t.valueBadge}
                </p>
              )}

              <button
                type="button"
                onClick={() => handleAction(t)}
                data-testid={`${t.testid}-cta`}
                className={`mt-9 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium transition-all active:scale-[0.97] ${
                  t.accent
                    ? "bg-[#D4A53A] text-[#0A1E3F] hover:bg-[#B58A26]"
                    : "bg-[#0A1E3F] text-[#F4F1EA] hover:bg-[#061226]"
                }`}
              >
                {t.cta} <ArrowRight size={14} />
              </button>
            </article>
          </Reveal>
        ))}
      </div>
    </div>

    <UpiModal open={!!pay} onClose={() => setPay(null)} plan={pay} />
  </section>
  );
};

/* ------------------------- Contact ------------------------- */
const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) {
      setError("Please fill all fields before sending.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setError(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Could not send your message. Please try again."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32">
      <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12">
        <Reveal>
          <Overline>08 · Let's talk</Overline>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-4 leading-[1.05]">
            Let's build your{" "}
            <span className="italic font-light text-[#D4A53A]">next chapter.</span>
          </h2>
          <p className="mt-6 text-[#1C1A17]/85 max-w-md font-serif italic text-lg">
            Tell us where you're stuck — positioning, messaging, launch strategy
            or storytelling — and we'll help you find the next step.
          </p>
          <p className="mt-4 text-[#1C1A17]/70 max-w-md text-[15px]">
            Whether you're shaping a new brand, refining your message, looking
            for training, or want to collaborate — drop a line. Replies usually
            within a working day.
          </p>
          <div className="mt-8 font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#6B655B] space-y-1">
            <p>· based in india, working globally</p>
            <p>· the.story.assembly</p>
          </div>
          <a
            href="mailto:hello@storyassembly.in"
            data-testid="contact-email"
            className="mt-4 inline-flex items-center gap-2 text-[15px] text-[#0A1E3F] hover:text-[#D4A53A] transition-colors font-medium"
          >
            hello@storyassembly.in
            <ArrowUpRight size={14} />
          </a>
          <a
            href={LINKS.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-linkedin"
            className="mt-3 inline-flex items-center gap-2 text-sm text-[#0A1E3F] hover:text-[#D4A53A] transition-colors"
          >
            <Linkedin size={16} /> Follow The Story Assembly on LinkedIn
            <ArrowUpRight size={14} />
          </a>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="border border-[#DCD7C9] rounded-md p-7 md:p-8 bg-[#F4F1EA]"
          >
            <label className="block">
              <span className="font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#6B655B]">
                Name
              </span>
              <input
                data-testid="contact-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                className="mt-2 w-full bg-transparent border-b border-[#DCD7C9] focus:border-[#1C1A17] focus:outline-none py-2 text-lg"
              />
            </label>
            <label className="block mt-5">
              <span className="font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#6B655B]">
                Email
              </span>
              <input
                data-testid="contact-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@brand.com"
                className="mt-2 w-full bg-transparent border-b border-[#DCD7C9] focus:border-[#1C1A17] focus:outline-none py-2 text-lg"
              />
            </label>
            <label className="block mt-5">
              <span className="font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#6B655B]">
                Message
              </span>
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                placeholder="Tell me about your brand or project…"
                className="mt-2 w-full bg-transparent border-b border-[#DCD7C9] focus:border-[#1C1A17] focus:outline-none py-2 text-[15px] resize-none"
              />
            </label>

            <div className="mt-7">
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#0A1E3F] text-[#F4F1EA] text-sm font-medium hover:bg-[#061226] disabled:opacity-50 transition-all active:scale-[0.97]"
              >
                {sending ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Start your story <ArrowRight size={14} />
                  </>
                )}
              </button>
            </div>

            {sent && (
              <p
                data-testid="contact-success"
                className="mt-5 text-[14px] text-[#0A1E3F]"
              >
                ✓ Got it. We'll reply within a working day.
              </p>
            )}
            {error && (
              <p data-testid="contact-error" className="mt-5 text-[14px] text-[#D4A53A]">
                {error}
              </p>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
};

/* ------------------------- Footer ------------------------- */
const FooterNewsletter = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [errMsg, setErrMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    setErrMsg("");
    try {
      const res = await fetch(`${API}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Couldn't subscribe");
      }
      setStatus("done");
      setEmail("");
    } catch (err) {
      setErrMsg(err.message || "Something went wrong");
      setStatus("error");
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="footer-newsletter-form"
      className="mt-4 md:mt-0 md:max-w-sm w-full"
    >
      <label className="font-mono-display text-[10px] uppercase tracking-[0.22em] text-[#6B655B]">
        Get a brand essay in your inbox · monthly
      </label>
      {status === "done" ? (
        <div
          data-testid="footer-newsletter-success"
          className="mt-3 inline-flex items-center gap-2 text-[13px] text-[#0A1E3F] font-mono-display tracking-wider"
        >
          <Check size={14} className="text-[#D4A53A]" /> You're on the list.
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@brand.com"
            data-testid="footer-newsletter-email"
            className="flex-1 rounded-md border border-[#DCD7C9] bg-white px-3.5 py-2.5 text-[14px] focus:border-[#0A1E3F] focus:outline-none min-w-0"
          />
          <button
            type="submit"
            disabled={status === "loading"}
            data-testid="footer-newsletter-submit"
            className="inline-flex items-center justify-center rounded-md px-4 py-2.5 bg-[#0A1E3F] text-[#F4F1EA] text-[13px] font-medium hover:bg-[#0A1E3F]/90 disabled:opacity-60 transition-all"
          >
            {status === "loading" ? <Loader2 size={14} className="animate-spin" /> : "Subscribe"}
          </button>
        </div>
      )}
      {status === "error" && (
        <p data-testid="footer-newsletter-error" className="mt-2 text-[12px] text-red-700">
          {errMsg}
        </p>
      )}
    </form>
  );
};

const MobileStickyCTA = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      // Show after scrolling past hero (~600px), hide near footer
      const y = window.scrollY;
      const footerTop = document.querySelector("footer")?.offsetTop ?? Infinity;
      const viewportBottom = y + window.innerHeight;
      setVisible(y > 600 && viewportBottom < footerTop + 80);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      data-testid="mobile-sticky-cta"
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 pointer-events-none transition-all duration-300 ${
        visible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
      }`}
    >
      <div className="pointer-events-auto mx-3 mb-3 rounded-full bg-[#0A1E3F] shadow-2xl border border-[#D4A53A]/30 px-2 py-2 flex items-center gap-2">
        <a
          href="#demo"
          data-testid="mobile-cta-demo"
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 bg-[#D4A53A] text-[#0A1E3F] text-[13px] font-medium"
        >
          <Sparkles size={13} /> Try the demo
        </a>
        <a
          href="#pricing"
          data-testid="mobile-cta-pricing"
          className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-[#F4F1EA] text-[13px] border border-[#F4F1EA]/20"
        >
          Plans
        </a>
      </div>
    </div>
  );
};

const Footer = () => (
  <footer className="border-t border-[#DCD7C9] py-14 bg-[#F4F1EA]">
    <div className="max-w-[1440px] mx-auto px-6 md:px-10">
      <div className="flex flex-col md:flex-row gap-6 md:items-end justify-between">
        <div>
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_tale-builder-110/artifacts/rxbavk25_ChatGPT%20Image%20May%2029%2C%202025%2C%2010_23_27%20PM.png"
              alt="The Story Assembly"
              className="h-9 w-9 rounded-md object-cover ring-1 ring-[#1C1A17]/10"
            />
            <span className="font-serif text-xl tracking-tight">
              The <span className="italic font-light">Story</span>{" "}
              <span className="text-[#D4A53A]">Assembly</span>
            </span>
          </div>
          <p className="font-serif italic text-[#1C1A17]/70 mt-2 text-lg">
            Storytelling, structured.
          </p>
        </div>
        <div className="font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#6B655B] flex flex-col items-stretch md:items-end gap-5 md:gap-4">
          <FooterNewsletter />
          <div className="flex flex-wrap items-center gap-5">
            <a
              href="mailto:hello@storyassembly.in"
              data-testid="footer-email"
              className="inline-flex items-center gap-1.5 hover:text-[#0A1E3F] transition-colors normal-case tracking-normal text-sm"
            >
              hello@storyassembly.in
            </a>
            <a
              href={LINKS.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin"
              className="inline-flex items-center gap-1.5 hover:text-[#0A1E3F] transition-colors"
            >
              <Linkedin size={13} /> LinkedIn
            </a>
          </div>
        </div>
      </div>
      <div className="mt-10 pt-6 border-t border-[#DCD7C9] flex flex-col md:flex-row gap-4 md:items-center justify-between">
        <div className="flex flex-wrap gap-x-6 gap-y-2 font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#6B655B]">
          <a
            href="/examples/nike"
            data-testid="footer-example-nike"
            className="hover:text-[#0A1E3F] transition-colors"
          >
            Nike Story
          </a>
          <a
            href="/blog"
            data-testid="footer-blog"
            className="hover:text-[#0A1E3F] transition-colors"
          >
            Journal
          </a>
          <a
            href="/brand-name-generator"
            data-testid="footer-tool-names"
            className="hover:text-[#0A1E3F] transition-colors"
          >
            Name Generator
          </a>
          <a
            href="/tagline-roulette"
            data-testid="footer-tool-taglines"
            className="hover:text-[#0A1E3F] transition-colors"
          >
            Tagline Roulette
          </a>
          <a
            href="/brand-vibe-check"
            data-testid="footer-tool-vibe"
            className="hover:text-[#0A1E3F] transition-colors"
          >
            Vibe Check
          </a>
          <a
            href="/privacy"
            data-testid="footer-privacy"
            className="hover:text-[#0A1E3F] transition-colors"
          >
            Privacy Policy
          </a>
          <a
            href="/terms"
            data-testid="footer-terms"
            className="hover:text-[#0A1E3F] transition-colors"
          >
            Terms of Service
          </a>
          <a
            href="/refund"
            data-testid="footer-refund"
            className="hover:text-[#0A1E3F] transition-colors"
          >
            Refund Policy
          </a>
        </div>
        <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
          © 2025 The Story Assembly · All rights reserved
        </div>
      </div>
    </div>
  </footer>
);

/* ------------------------- App ------------------------- */
function App() {
  const [pro, setPro] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(PRO_INFO_KEY) || "null");
    } catch {
      return null;
    }
  });

  // Healthcheck + analytics beacon + magic-link redemption
  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
    if (!sessionStorage.getItem("sa_visit_logged")) {
      axios
        .post(`${API}/track-visit`, { path: window.location.pathname })
        .then(() => sessionStorage.setItem("sa_visit_logged", "1"))
        .catch(() => {});
    }

    // If the URL has ?pro=<token>, validate and store it, then strip from the URL
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("pro");
    if (urlToken) {
      axios
        .post(`${API}/pro/redeem`, { token: urlToken })
        .then(({ data }) => {
          localStorage.setItem(PRO_TOKEN_KEY, urlToken);
          localStorage.setItem(PRO_INFO_KEY, JSON.stringify(data));
          setPro(data);
          // Tell other components (Demo) to re-fetch quota. Dispatch twice
          // to cover React 18's mount/strict-mode race.
          window.dispatchEvent(new Event("sa:pro-changed"));
          setTimeout(() => window.dispatchEvent(new Event("sa:pro-changed")), 400);
        })
        .catch(() => {
          // invalid token — clear any stale local copy
          localStorage.removeItem(PRO_TOKEN_KEY);
          localStorage.removeItem(PRO_INFO_KEY);
        })
        .finally(() => {
          params.delete("pro");
          const cleanUrl =
            window.location.pathname +
            (params.toString() ? "?" + params.toString() : "") +
            window.location.hash;
          window.history.replaceState({}, "", cleanUrl);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="App bg-[#F4F1EA] text-[#1C1A17] min-h-screen">
      <Header proName={pro ? (pro.name || "").split(" ")[0] : null} />
      <main>
        <Hero />
        <Marquee />
        <ExampleOutput />
        <Demo />
        <Features />
        <NotAnother />
        <Founder />
        <TrustMetrics />
        <SocialProof />
        <FAQ />
        <LeadMagnet />
        <Pricing />
        <Contact />
      </main>
      <Footer />
      <MobileStickyCTA />
    </div>
  );
}

export default App;
